import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DraggableChildrenFn, DraggingStyle } from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';

export function useDraggableInPortal() {
  const self = useRef<{ elt?: Element }>({}).current;

  useEffect(() => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.pointerEvents = 'none';
    div.style.top = '0';
    div.style.width = '100%';
    div.style.height = '100%';
    self.elt = div;
    document.body.appendChild(div);
    return () => {
      document.body.removeChild(div);
    };
  }, [self]);

  return (render: DraggableChildrenFn): DraggableChildrenFn =>
    (provided, ...args) => {
      const element = render(provided, ...args);
      if (
        (provided.draggableProps?.style as DraggingStyle)?.position === 'fixed'
      ) {
        return createPortal(element, self.elt ?? new Element());
      }
      return element;
    };
}

export function useOnChange<T>(
  callback: ((x: T) => void) | (() => void),
  value: T,
  options?: {
    callOnMount?: boolean;
    strinfigy?: boolean;
    layoutEffect?: boolean;
  }
): void {
  const [previousValue, setPreviousValue] = useState(value);
  const [firstCallPending, setFirstCallPending] = useState(
    options?.callOnMount ?? false
  );
  const checkEquals: (a: T, b: T) => boolean = options?.strinfigy
    ? (a, b) => JSON.stringify(a) === JSON.stringify(b)
    : (a, b) => a === b;
  const hook = options?.layoutEffect ? useLayoutEffect : useEffect;
  hook(() => {
    if (firstCallPending) {
      callback(value);
      setFirstCallPending(false);
      setPreviousValue(value);
    } else if (!checkEquals(value, previousValue)) {
      callback(value);
      setPreviousValue(value);
    }
  }, [callback, checkEquals, firstCallPending, previousValue, value]);
}

export default {
  useDraggableInPortal,
};
